/**
 * Customer Support Widget
 * Displays available support methods with quick access
 *
 * Features:
 * - Shows all support channels (video call, live chat, forms, help centre)
 * - Compact card shows 24/7 availability + quick preview
 * - Modal displays all support methods with action buttons
 * - Integrates with existing drawers (HelpDrawer, videoCallDrawer)
 * - Links to external forms and help centre
 * - British English throughout
 * - All SVG icons inline
 * - Theme token styling
 *
 * @version 1.0.0
 */

(function(window) {
  'use strict';

  class CustomerSupportWidget extends window.ProductWidgets.BaseWidget {
    constructor(manager, container, config) {
      super(manager, container, config);

      this.modalId = `support-modal-${config.id}`;

      // Check if video call is available (Showroom_Yes tag + product available)
      this.videoCallAvailable = this.checkVideoCallAvailability();

      // Content (can be customised via block settings)
      this.content = {
        cardHeading: config.cardHeading || 'Customer Support',
        cardSubheading: config.cardSubheading || 'We\'re here to help',
        viewButton: config.viewButton || 'Get Help',
        modalHeading: config.modalHeading || 'How Can We Help?',
        modalSubheading: config.modalSubheading || 'Choose your preferred support method',
        helpCentreUrl: config.helpCentreUrl || 'https://help.riiroo.com'
      };
    }

    /**
     * Check if video call is available
     * Only available if product has Showroom_Yes tag and is in stock
     */
    checkVideoCallAvailability() {
      if (!this.productData || !this.productData.tags) {
        return false;
      }

      const hasShowroomTag = this.productData.tags.some(tag =>
        tag.trim() === 'Showroom_Yes'
      );

      const isAvailable = this.productData.available === true;

      return hasShowroomTag && isAvailable;
    }

    /**
     * Initialize widget
     */
    async init() {
      // Widget is always available - no eligibility checks needed
    }

    /**
     * Render widget to container
     */
    render() {
      // Build card + modal inline (x-modal pattern)
      const html = `
        ${this.buildCardHTML()}
        ${this.buildModalHTML()}
      `;

      // Use setContent() to properly handle skeleton loading
      this.setContent(html);

      // Attach event listeners
      this.attachEventListeners();

      this.emit('customer-support:rendered');
    }

    /**
     * Build widget card HTML
     */
    buildCardHTML() {
      return `
        <div class="pw-widget pw-customer-support-widget" style="width: 100%;">
          <div class="pw-widget--card pw-widget--card-clickable" style="padding: 16px; cursor: pointer;"
               data-modal-trigger="${this.modalId}">

            <!-- Header with Icon -->
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: rgb(var(--color-base-button) / 0.1); border-radius: var(--rounded-button);">
                ${this.getSupportIconSVG()}
              </div>
              <div style="flex: 1;">
                <h3 class="pw-text-lg pw-font-bold" style="margin: 0;">${this.content.cardHeading}</h3>
                <p class="pw-text-sm" style="margin: 0; color: rgb(var(--color-drawer-text) / 0.7);">
                  ${this.content.cardSubheading}
                </p>
              </div>
            </div>

            <!-- Support Methods Badge -->
            <div style="margin-bottom: 16px;">
              <div style="display: inline-flex; align-items: center; gap: 8px; padding: 8px 16px; background: rgb(var(--color-base-button) / 0.1); border: 2px solid rgb(var(--color-base-button) / 0.3); border-radius: var(--rounded-button);">
                ${this.getCheckCircleIconSVG()}
                <span style="font-weight: 700; font-size: 0.95rem; color: rgb(var(--color-base-button));">
                  ${this.videoCallAvailable ? '4' : '3'} Support Options
                </span>
              </div>
            </div>

            <!-- Quick Preview -->
            <div style="display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px;">
              <div style="display: flex; align-items: start; gap: 8px;">
                ${this.getCheckIconSVG()}
                <span class="pw-text-sm" style="color: rgb(var(--color-drawer-text) / 0.9);">
                  Live chat - instant responses
                </span>
              </div>
              ${this.videoCallAvailable ? `
                <div style="display: flex; align-items: start; gap: 8px;">
                  ${this.getCheckIconSVG()}
                  <span class="pw-text-sm" style="color: rgb(var(--color-drawer-text) / 0.9);">
                    Video call consultations
                  </span>
                </div>
              ` : ''}
              <div style="display: flex; align-items: start; gap: 8px;">
                ${this.getCheckIconSVG()}
                <span class="pw-text-sm" style="color: rgb(var(--color-drawer-text) / 0.9);">
                  24/7 self-service help centre
                </span>
              </div>
            </div>

            <!-- Contact Us Button -->
            <div style="display: flex; justify-content: flex-end;">
              <button
                type="button"
                class="pw-learn-more"
                aria-controls="${this.modalId}"
                aria-expanded="false"
              >
                <span>${this.content.viewButton}</span>
                <svg class="pw-learn-more__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>

          </div>
        </div>
      `;
    }

    /**
     * Build modal HTML using x-modal pattern
     */
    buildModalHTML() {
      return `
        <x-modal id="${this.modalId}" class="x-modal drawer z-30 fixed bottom-0 left-0 h-full w-full pointer-events-none" role="dialog" aria-modal="true" aria-label="Customer Support Options" hidden>
          <overlay-element class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none" aria-controls="${this.modalId}" aria-expanded="false"></overlay-element>
          <div class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden">
            <gesture-element class="drawer__header flex items-center justify-between opacity-0 invisible relative">
              <span class="drawer__title heading font-bold title-md">${this.content.cardHeading}</span>
              <button class="button button--secondary button--close drawer__close hidden sm:flex items-center justify-center" type="button" is="hover-button" aria-controls="${this.modalId}" aria-expanded="false" aria-label="Close">
                <span class="btn-fill" data-fill></span>
                <span class="btn-text">
                  <svg class="icon icon-close icon-sm" aria-hidden="true" focusable="false" role="presentation" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 14" fill="none">
                    <path d="M15 1.00012L1 13.0001M1 1L15 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                  </svg>
                </span>
              </button>
            </gesture-element>
            <div class="drawer__content opacity-0 invisible flex flex-col h-full grow shrink">
              <div class="drawer__scrollable relative flex flex-col gap-5 md:gap-7 grow shrink">

                <!-- Hero Section -->
                <div class="text-center">
                  <div style="display: inline-flex; align-items: center; justify-content: center; width: 4rem; height: 4rem; background: rgb(var(--color-base-button)); border-radius: var(--rounded-card); margin-bottom: 1rem;">
                    ${this.getSupportIconSVG('var(--color-base-button-text)')}
                  </div>
                  <h3 class="heading font-bold text-xl md:text-2xl" style="margin-bottom: 0.75rem;">
                    ${this.content.modalHeading}
                  </h3>
                  <p class="text-base text-opacity" style="max-width: 500px; margin: 0 auto;">
                    ${this.content.modalSubheading}
                  </p>
                </div>

                <!-- Support Methods List -->
                <div style="display: flex; flex-direction: column; gap: 1rem;">

                  <!-- Live Chat -->
                  <div style="padding: 1rem; background: rgb(var(--color-drawer-background)); border: 2px solid rgb(var(--color-drawer-text) / 0.12); border-radius: var(--rounded-card); transition: border-color 0.2s ease;">
                    <div style="display: flex; align-items: start; gap: 0.75rem; margin-bottom: 0.75rem;">
                      <div style="flex-shrink: 0; width: 2.5rem; height: 2.5rem; display: flex; align-items: center; justify-content: center; background: rgb(var(--color-base-button) / 0.1); border-radius: var(--rounded-button);">
                        ${this.getChatBubbleIconSVG()}
                      </div>
                      <div style="flex: 1;">
                        <h4 style="font-size: 0.9375rem; font-weight: 600; margin: 0 0 0.25rem 0; color: rgb(var(--color-drawer-text));">
                          Live Chat
                        </h4>
                        <p style="font-size: 0.8125rem; margin: 0; color: rgb(var(--color-drawer-text) / 0.7); line-height: 1.5;">
                          Instant messaging - fastest response time
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      class="pw-btn pw-btn--secondary pw-support-action-btn"
                      data-action="crisp"
                    >
                      Start Chat
                    </button>
                  </div>

                  ${this.videoCallAvailable ? `
                  <!-- Video Call (conditional) -->
                  <div style="padding: 1rem; background: rgb(var(--color-drawer-background)); border: 2px solid rgb(var(--color-drawer-text) / 0.12); border-radius: var(--rounded-card);">
                    <div style="display: flex; align-items: start; gap: 0.75rem; margin-bottom: 0.75rem;">
                      <div style="flex-shrink: 0; width: 2.5rem; height: 2.5rem; display: flex; align-items: center; justify-content: center; background: rgb(var(--color-base-button) / 0.1); border-radius: var(--rounded-button);">
                        ${this.getMethodIcon('video')}
                      </div>
                      <div style="flex: 1;">
                        <h4 style="font-size: 0.9375rem; font-weight: 600; margin: 0 0 0.25rem 0; color: rgb(var(--color-drawer-text));">
                          Video Call Consultation
                        </h4>
                        <p style="font-size: 0.8125rem; margin: 0; color: rgb(var(--color-drawer-text) / 0.7); line-height: 1.5;">
                          Book a call for product demos and technical support
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      class="pw-btn pw-btn--secondary pw-support-action-btn"
                      data-action="videoCallDrawer"
                    >
                      Book Video Call
                    </button>
                  </div>
                  ` : ''}

                  <!-- Support Form -->
                  <div style="padding: 1rem; background: rgb(var(--color-drawer-background)); border: 2px solid rgb(var(--color-drawer-text) / 0.12); border-radius: var(--rounded-card);">
                    <div style="display: flex; align-items: start; gap: 0.75rem; margin-bottom: 0.75rem;">
                      <div style="flex-shrink: 0; width: 2.5rem; height: 2.5rem; display: flex; align-items: center; justify-content: center; background: rgb(var(--color-base-button) / 0.1); border-radius: var(--rounded-button);">
                        ${this.getMethodIcon('form')}
                      </div>
                      <div style="flex: 1;">
                        <h4 style="font-size: 0.9375rem; font-weight: 600; margin: 0 0 0.25rem 0; color: rgb(var(--color-drawer-text));">
                          Support Form
                        </h4>
                        <p style="font-size: 0.8125rem; margin: 0; color: rgb(var(--color-drawer-text) / 0.7); line-height: 1.5;">
                          For detailed enquiries with file attachments
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      class="pw-btn pw-btn--secondary pw-support-action-btn"
                      data-action="HelpDrawer"
                    >
                      Open Form
                    </button>
                  </div>

                  <!-- Help Centre -->
                  <div style="padding: 1rem; background: rgb(var(--color-drawer-background)); border: 2px solid rgb(var(--color-drawer-text) / 0.12); border-radius: var(--rounded-card);">
                    <div style="display: flex; align-items: start; gap: 0.75rem; margin-bottom: 0.75rem;">
                      <div style="flex-shrink: 0; width: 2.5rem; height: 2.5rem; display: flex; align-items: center; justify-content: center; background: rgb(var(--color-base-button) / 0.1); border-radius: var(--rounded-button);">
                        ${this.getMethodIcon('book')}
                      </div>
                      <div style="flex: 1;">
                        <h4 style="font-size: 0.9375rem; font-weight: 600; margin: 0 0 0.25rem 0; color: rgb(var(--color-drawer-text));">
                          Help Centre
                        </h4>
                        <p style="font-size: 0.8125rem; margin: 0; color: rgb(var(--color-drawer-text) / 0.7); line-height: 1.5;">
                          Assembly guides, troubleshooting, and FAQs
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      class="pw-btn pw-btn--secondary pw-support-action-btn"
                      data-action="${this.content.helpCentreUrl}"
                    >
                      Visit Help Centre
                    </button>
                  </div>

                </div>

              </div>
            </div>
          </div>
        </x-modal>
      `;
    }


    /**
     * Attach event listeners
     */
    attachEventListeners() {
      // Make entire card clickable
      const cardElement = this.container.querySelector('[data-modal-trigger]');
      if (cardElement) {
        cardElement.addEventListener('click', (e) => {
          // Don't trigger if clicking the button directly
          if (e.target.closest('.pw-learn-more')) {
            return;
          }
          this.openModal();
        });
      }

      // Modal trigger button (still works if clicked directly)
      const learnMoreBtn = this.container.querySelector('.pw-learn-more');
      if (learnMoreBtn) {
        learnMoreBtn.addEventListener('click', () => this.openModal());
      }

      // Support action buttons
      const actionBtns = this.container.querySelectorAll('.pw-support-action-btn');
      actionBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          const action = btn.dataset.action;
          this.handleSupportAction(action);
        });
      });
    }

    /**
     * Handle support action based on type
     */
    handleSupportAction(action) {
      if (action === 'HelpDrawer' || action === 'videoCallDrawer') {
        // Open Shopify drawer
        const drawer = document.getElementById(action);
        if (drawer && drawer.show) {
          // Close current modal first
          this.closeModal();
          // Small delay to allow modal to close before opening drawer
          setTimeout(() => {
            drawer.show();
          }, 300);
        }
      } else if (action === 'crisp') {
        // Open Crisp chat
        if (window.$crisp) {
          this.closeModal();
          window.$crisp.push(['do', 'chat:open']);
        }
      } else if (action.startsWith('http')) {
        // Open external link in new tab
        window.open(action, '_blank');
      }
    }

    /**
     * Open modal
     */
    openModal() {
      const modal = document.getElementById(this.modalId);
      if (modal && modal.show) {
        modal.show();
      }
    }

    /**
     * Close modal
     */
    closeModal() {
      const modal = document.getElementById(this.modalId);
      if (modal && modal.hide) {
        modal.hide();
      }
    }

    /**
     * Get method icon based on type
     */
    getMethodIcon(type) {
      switch (type) {
        case 'video':
          return `<svg style="width: 1.5rem; height: 1.5rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg>`;
        case 'chat':
          return `<svg style="width: 1.5rem; height: 1.5rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path></svg>`;
        case 'form':
          return `<svg style="width: 1.5rem; height: 1.5rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>`;
        case 'book':
          return `<svg style="width: 1.5rem; height: 1.5rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path></svg>`;
        case 'tool':
          return `<svg style="width: 1.5rem; height: 1.5rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>`;
        case 'tag':
          return `<svg style="width: 1.5rem; height: 1.5rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path></svg>`;
        default:
          return this.getSupportIconSVG();
      }
    }

    /**
     * SVG Icons
     */

    /**
     * Get support/headset icon
     */
    getSupportIconSVG(colorRGB = 'var(--color-base-button)') {
      return `
        <svg style="width: 20px; height: 20px; color: rgb(${colorRGB});" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"></path>
        </svg>
      `;
    }

    /**
     * Get clock icon
     */
    getClockIconSVG() {
      return `
        <svg style="width: 16px; height: 16px; flex-shrink: 0; color: rgb(34, 197, 94);" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      `;
    }

    /**
     * Get check icon
     */
    getCheckIconSVG() {
      return `
        <svg style="width: 16px; height: 16px; flex-shrink: 0; margin-top: 2px; color: rgb(var(--color-base-button));" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      `;
    }

    /**
     * Get arrow icon
     */
    getArrowIconSVG() {
      return `
        <svg style="width: 18px; height: 18px;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
        </svg>
      `;
    }

    /**
     * Get phone icon
     */
    getPhoneIconSVG(colorRGB = 'var(--color-base-button)') {
      return `
        <svg style="width: 18px; height: 18px; color: rgb(${colorRGB});" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
        </svg>
      `;
    }

    /**
     * Get chat icon (small)
     */
    getChatIconSVG() {
      return `
        <svg style="width: 18px; height: 18px; flex-shrink: 0; color: rgb(var(--color-base-button));" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
        </svg>
      `;
    }

    /**
     * Get video icon (small)
     */
    getVideoIconSVG() {
      return `
        <svg style="width: 18px; height: 18px; flex-shrink: 0; color: rgb(var(--color-base-button));" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
        </svg>
      `;
    }

    /**
     * Get chat bubble icon (for modal) - uses theme tokens
     */
    getChatBubbleIconSVG() {
      return `
        <svg style="width: 1.25rem; height: 1.25rem; color: rgb(var(--color-base-button));" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
        </svg>
      `;
    }

    /**
     * Get check circle icon
     */
    getCheckCircleIconSVG() {
      return `
        <svg style="width: 16px; height: 16px; flex-shrink: 0; color: rgb(var(--color-base-button));" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      `;
    }

    /**
     * Get external link icon
     */
    getExternalLinkIconSVG() {
      return `
        <svg style="width: 18px; height: 18px;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
        </svg>
      `;
    }

    /**
     * Get method icon based on type
     */
    getMethodIcon(type) {
      switch (type) {
        case 'video':
          return `<svg style="width: 1.5rem; height: 1.5rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg>`;
        case 'form':
          return `<svg style="width: 1.5rem; height: 1.5rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>`;
        case 'book':
          return `<svg style="width: 1.5rem; height: 1.5rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path></svg>`;
        default:
          return this.getSupportIconSVG();
      }
    }
  }

  // Register widget with ProductWidgets system
  if (window.ProductWidgets && window.ProductWidgets.widgets) {
    window.ProductWidgets.widgets.CustomerSupportWidget = CustomerSupportWidget;
  } else {
    console.error('[ProductWidgets] ProductWidgets system not available');
  }

})(window);
